import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { computed } from 'mobx'
import { task } from 'mobx-task'
import type { JobListViewModelFactory } from '@/features/jobs/list/JobListViewModel'

/**
 * View-model for the All Jobs view.
 */
export class AllJobsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly currentJobListVm

  readonly completedJobListVm

  constructor(createJobListViewModel: JobListViewModelFactory) {
    this.currentJobListVm = createJobListViewModel({
      limit: 20,
      models: (jobs) => jobs.filter((j) => j.status !== 'Closed'),
    })
    this.completedJobListVm = createJobListViewModel({
      limit: 20,
      models: (jobs) => jobs.filter((j) => j.status === 'Closed'),
    })

    this._lifecycle.manage(this.currentJobListVm)
    this._lifecycle.manage(this.completedJobListVm)
  }

  @computed get isEmpty() {
    return this.currentJobs.length === 0 && this.completedJobs.length === 0
  }

  @computed get currentJobs() {
    return this.currentJobListVm.jobVms
  }

  @computed get completedJobs() {
    return this.completedJobListVm.jobVms
  }

  @computed get totalCurrentJobs() {
    return this.currentJobListVm.total
  }

  @computed get totalCompletedJobs() {
    return this.completedJobListVm.total
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    const signal = this._lifecycle.signal
    void this.fetchCurrent(signal)
    void this.fetchCompleted(signal)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches current jobs.
   */
  fetchCurrent = task(async (signal: AbortSignal) => {
    const openStatuses = [
      'UNDER_CONSTRUCTION',
      'UNCLAIMED',
      'CLAIMED',
      'INFO_GATHERING',
      'ON_HOLD',
      'IDLE',
    ]
    const openStatusFilter = openStatuses
      .map((s) => `status = "${s}"`)
      .join(' OR ')
    await this.currentJobListVm.fetch({
      signal,
      staleWhileRevalidate: true,
      filter: openStatusFilter,
      orderBy: 'date_last_activity desc',
    })
  })

  /**
   * Fetches completed jobs.
   */
  fetchCompleted = task(async (signal: AbortSignal) => {
    await this.completedJobListVm.fetch({
      signal,
      staleWhileRevalidate: true,
      filter: `status = "CLOSED"`,
      orderBy: 'date_completed desc',
    })
  })
}
